// Endpoints de canal — `/v1/metricas/canal/*` (MET-22..MET-25).
//
// ⚠️ `canal/saude` e `canal/restricao` são DOIS endpoints, não um payload filtrado
// por papel. `MetricsNumberRestrictionDto` é `{ restricted, since }` e nunca
// contém `qualityRating`: um erro de serialização não consegue vazar saúde do
// número para um vendedor porque o valor jamais entra naquele DTO. No front, o
// que garante isso é o `skip` — só GESTOR chama `metricsNumberHealth`.
import { api } from '@/lib/api';
import type {
  MetricsCollection,
  MetricsNumberHealthDto,
  MetricsNumberRestrictionDto,
  MetricsTemplateStatsDto,
  MetricsWindowDto,
  Segmented,
  WithEnvelope,
} from '../model';
import { metricsUrl, type MetricsQueryArgs, type MetricsSegmentArgs } from './shared';

export const metricsChannelApi = api.injectEndpoints({
  endpoints: (build) => ({
    /** MET-22 — GESTOR apenas. */
    metricsNumberHealth: build.query<
      WithEnvelope<MetricsCollection<MetricsNumberHealthDto>>,
      MetricsQueryArgs
    >({
      query: (args) => metricsUrl('canal/saude', { ...args }),
      providesTags: ['MetricsWaHealth'],
    }),
    /** MET-22 — todos os papéis; só diz SE há restrição, nunca a nota. */
    metricsNumberRestriction: build.query<
      WithEnvelope<MetricsNumberRestrictionDto>,
      MetricsQueryArgs
    >({
      query: (args) => metricsUrl('canal/restricao', { ...args }),
      providesTags: ['MetricsWaHealth'],
    }),
    /** MET-23 — entrega/leitura por template. */
    metricsTemplateStats: build.query<
      WithEnvelope<MetricsCollection<MetricsTemplateStatsDto>>,
      MetricsQueryArgs
    >({
      query: (args) => metricsUrl('canal/templates', { ...args }),
      providesTags: ['MetricsTemplateStats'],
    }),
    metricsCampaigns: build.query<WithEnvelope<Segmented>, MetricsSegmentArgs>({
      query: (args) => metricsUrl('canal/campanhas', { ...args }),
      providesTags: ['MetricsTemplateStats'],
    }),
    /** MET-24 — opt-out / bloqueio. */
    metricsOptOut: build.query<WithEnvelope<Segmented>, MetricsSegmentArgs>({
      query: (args) => metricsUrl('canal/optout', { ...args }),
      providesTags: ['MetricsAggregate'],
    }),
    /** MET-25 — janela de 24h. */
    metricsWindow: build.query<WithEnvelope<MetricsWindowDto>, MetricsQueryArgs>({
      query: (args) => metricsUrl('canal/janela-24h', { ...args }),
      providesTags: ['MetricsRealtime'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMetricsNumberHealthQuery,
  useMetricsNumberRestrictionQuery,
  useMetricsTemplateStatsQuery,
  useMetricsCampaignsQuery,
  useMetricsOptOutQuery,
  useMetricsWindowQuery,
} = metricsChannelApi;
